import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    IBaseCRUModalComponent,
    IBaseCRUModalResultBase
} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {FileEntryModelExtension} from '../../../api-extensions/models/file-entry.model-extension';
import {UserDataCellDisplayComponent} from '../../../data-cell-display/user-data-cell-display/user-data-cell-display.component';
import {FileUtils} from '../../../utils/file.utils';
import {CpmCFileApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {firstValueFrom} from 'rxjs';

export class FileDetailsModalConfig {
    constructor(public fileEntry: FileEntryModelExtension) {}
}

export class FileDetailsModalResult {
    constructor(public item?: FileEntryModelExtension) {}
}

/**
 * File Details modal - displays file/directory information
 */
@Component({
    selector: 'app-file-details-modal',
    templateUrl: './file-details-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule, UserDataCellDisplayComponent]
})
export class FileDetailsModalComponent extends IBaseCRUModalComponent<
    FileEntryModelExtension,
    FileDetailsModalComponent,
    FileDetailsModalConfig,
    FileDetailsModalResult
> {
    protected fileEntry!: FileEntryModelExtension;
    protected originalName!: string;
    protected resolvedPath = '';

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('Details', (model: FileEntryModelExtension) => model.name ?? '<Unknown>');
        this.config.width = '35rem';
        this.contentMinHeight = '15rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.fileEntry = this.config.data!.extended!.fileEntry;
        this.originalName = this.fileEntry.name;

        // Update header to include filename
        this.config.header = `Details of : ${this.fileEntry.name}`;

        // Fetch the human-readable resolved path
        try {
            this.resolvedPath = await firstValueFrom(CpmCFileApiService.getResolvedUserFilePath(this.fileEntry.uuid));
        } catch {
            // Fallback to constructing path if API fails
            this.resolvedPath = this.fileEntry.parent_path
                ? `${this.fileEntry.parent_path}/${this.fileEntry.name}`
                : `/${this.fileEntry.name}`;
        }

        // Create form with name field validation
        this.form = new FormGroup({
            name: new FormControl<string>(this.fileEntry.name, [
                Validators.required,
                Validators.pattern(FileUtils.FOLDER_NAME_REGEX)
            ])
        });
    }

    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Get file type display string
     * @returns File type description
     */
    protected getFileType(): string {
        if (this.fileEntry.is_dir) {
            return '[Folder]';
        }

        const extension = this.getFileExtension();
        return extension ? `[${extension.toUpperCase()} File]` : '[File]';
    }

    /**
     * Get file extension from filename
     * @returns File extension without dot, or empty string
     */
    protected getFileExtension(): string {
        const fileName = this.fileEntry.name;
        const lastDot = fileName.lastIndexOf('.');

        if (lastDot > 0 && lastDot < fileName.length - 1) {
            return fileName.substring(lastDot + 1);
        }

        return '';
    }

    /**
     * Get human-readable full path
     * @returns Complete file path
     */
    protected getHumanReadablePath(): string {
        return this.resolvedPath || `/${this.fileEntry.name}`;
    }

    /**
     * Get file/folder icon class
     * @returns PrimeNG icon class
     */
    protected getFileIcon(): string {
        return this.fileEntry.is_dir ? 'pi pi-folder' : 'pi pi-file';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On submit - rename the file/folder
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        const newName = this.form.controls['name'].value?.trim();
        if (!newName || newName === this.fileEntry.name) {
            // No change, just close
            this.ref.close();
            return;
        }

        this.showLoading = true;

        try {
            const updatedFileEntry = await firstValueFrom(
                CpmCFileApiService.patchUpdateUserFile(this.fileEntry.uuid, {
                    new_name: newName
                })
            );

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                summary: 'File renamed',
                detail: `Successfully renamed to "${newName}"`
            });

            // Return the updated file entry with proper result structure
            this.ref.close(
                Promise.resolve(
                    new IBaseCRUModalResultBase<FileDetailsModalResult>(
                        this.dialogOpenMode,
                        new FileDetailsModalResult(updatedFileEntry)
                    )
                )
            );
        } catch (err: unknown) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: 'Rename failed',
                detail: (err as {error?: {message?: string}})?.error?.message ?? 'Unknown error'
            });
        } finally {
            this.showLoading = false;
        }
    }

    /**
     * On cancel - close without changes
     */
    public override onCancel() {
        this.ref.close();
    }

    /**
     * Check if the name has been changed
     * @returns True if the name is different from the original
     */
    protected hasNameChanged(): boolean {
        const currentName = this.form?.controls['name'].value?.trim();
        return currentName !== this.originalName;
    }
}
